"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.appendSlashTrim = exports.wrapWeb3JsProvider = exports.validateRelayUrl = exports.averageBN = exports.adjustRelayRequestForPingResponse = exports.adjustGasCostParameterUp = exports.pickRandomElementFromArray = exports.waitForSuccess = exports.shuffle = exports.getERC165InterfaceID = exports.getRelayRequestID = exports.toNumber = exports.packRelayUrlForRegistrar = exports.splitRelayUrlForRegistrar = exports.formatTokenAmount = exports.removeNullValues = exports.signedTransactionToHash = exports.getDataAndSignature = exports.boolString = exports.getLatestEventData = exports.isSecondEventLater = exports.eventsComparator = exports.randomInRange = exports.ether = exports.sleep = exports.isSameAddress = exports.parseHexString = exports.getEcRecoverMeta = exports.calculateCalldataBytesZeroNonzero = exports.correctV = exports.getEip712Signature = exports.getDefaultMethodSuffix = exports.decodeRevertReason = exports.errorAsBoolean = exports.address2topic = exports.addresses2topics = exports.event2topic = exports.signatureRSV2Hex = exports.padTo64 = exports.removeHexPrefix = void 0;
const bn_js_1 = __importDefault(require("bn.js"));
const chalk_1 = __importDefault(require("chalk"));
const providers_1 = require("@ethersproject/providers");
const abi_1 = require("@ethersproject/abi");
const rlp_1 = require("rlp");
const tx_1 = require("@ethereumjs/tx");
const ethereumjs_util_1 = require("ethereumjs-util");
const Web3JSUtils_1 = require("./web3js/Web3JSUtils");
const ethers_1 = require("ethers");
const utils_1 = require("ethers/lib/utils");
function removeHexPrefix(hex) {
    if (hex == null || typeof hex.replace !== 'function') {
        throw new Error('Cannot remove hex prefix');
    }
    return hex.replace(/^0x/, '');
}
exports.removeHexPrefix = removeHexPrefix;
const zeroPad = '0000000000000000000000000000000000000000000000000000000000000000';
function padTo64(hex) {
    if (hex.length < 64) {
        hex = (zeroPad + hex).slice(-64);
    }
    return hex;
}
exports.padTo64 = padTo64;
function signatureRSV2Hex(r, s, v) {
    return '0x' + padTo64(r.toString('hex')) + padTo64(s.toString('hex')) + v.toString(16).padStart(2, '0');
}
exports.signatureRSV2Hex = signatureRSV2Hex;
function event2topic(contract, names) {
    // for testing: don't crash on mockup..
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!contract.filters) {
        return names;
    }
    return names
        .map(name => { return contract.filters[name]().topics[0]; });
}
exports.event2topic = event2topic;
function addresses2topics(addresses) {
    return addresses.map(address2topic);
}
exports.addresses2topics = addresses2topics;
function address2topic(address) {
    return '0x' + '0'.repeat(24) + address.toLowerCase().slice(2);
}
exports.address2topic = address2topic;
// This conversion is needed since WS provider returns error as false instead of null/undefined in (error,result)
function errorAsBoolean(err) {
    return err;
}
exports.errorAsBoolean = errorAsBoolean;
// extract revert reason from a revert bytes array.
function decodeRevertReason(revertBytes, throwOnError = false) {
    if (revertBytes == null) {
        return null;
    }
    if (!revertBytes.startsWith('0x08c379a0')) {
        if (revertBytes.includes('without a reason string') || revertBytes.includes('FWD: insufficient gas')) {
            revertBytes = 'Check Relay Worker balance - potentially Out Of Gas' + revertBytes;
        }
        if (throwOnError) {
            throw new Error('invalid revert bytes: ' + revertBytes);
        }
        return revertBytes;
    }
    return new abi_1.AbiCoder().decode(['string'], '0x' + revertBytes.slice(10))[0];
}
exports.decodeRevertReason = decodeRevertReason;
async function getDefaultMethodSuffix(provider) {
    const nodeInfo = await provider.send('web3_clientVersion', []);
    // ganache-cli
    if (nodeInfo.toLowerCase().includes('testrpc'))
        return '';
    // hardhat
    if (nodeInfo.toLowerCase().includes('hardhat'))
        return '_v4';
    // all other networks
    return '_v4';
}
exports.getDefaultMethodSuffix = getDefaultMethodSuffix;
async function getEip712Signature(provider, typedRequestData, methodSuffix = null, jsonStringifyRequest = false) {
    const senderAddress = typedRequestData.message.from;
    let dataToSign;
    if (jsonStringifyRequest) {
        dataToSign = JSON.stringify(typedRequestData);
    }
    else {
        dataToSign = typedRequestData;
    }
    methodSuffix = methodSuffix !== null && methodSuffix !== void 0 ? methodSuffix : await getDefaultMethodSuffix(provider);
    const paramBlock = {
        method: `eth_signTypedData${methodSuffix}`,
        params: [senderAddress, dataToSign],
        jsonrpc: '2.0',
        id: Date.now()
    };
    return await provider.send(paramBlock.method, paramBlock.params);
}
exports.getEip712Signature = getEip712Signature;
function correctV(result) {
    const buffer = (0, ethereumjs_util_1.toBuffer)(result);
    const last = buffer.length - 1;
    const oldV = buffer[last];
    if (oldV < 2) {
        buffer[last] += 27;
        console.warn(`signature V adjusted from ${oldV} to ${buffer[last]}`);
    }
    return (0, ethereumjs_util_1.bufferToHex)(buffer);
}
exports.correctV = correctV;
/**
 * @param calldata the hex string of data to be sent to the blockchain
 * @returns { calldataZeroBytes, calldataNonzeroBytes } - number of zero and nonzero bytes in the given calldata input
 */
function calculateCalldataBytesZeroNonzero(calldata) {
    const calldataBuf = Buffer.from(calldata.replace('0x', ''), 'hex');
    let calldataZeroBytes = 0;
    let calldataNonzeroBytes = 0;
    calldataBuf.forEach(ch => {
        ch === 0 ? calldataZeroBytes++ : calldataNonzeroBytes++;
    });
    return { calldataZeroBytes, calldataNonzeroBytes };
}
exports.calculateCalldataBytesZeroNonzero = calculateCalldataBytesZeroNonzero;
function getEcRecoverMeta(message, signature) {
    if (typeof signature === 'string') {
        const r = parseHexString(signature.substr(2, 65));
        const s = parseHexString(signature.substr(66, 65));
        const v = parseHexString(signature.substr(130, 2));
        signature = {
            v: v,
            r: r,
            s: s
        };
    }
    const bufSigned = (0, ethereumjs_util_1.hashPersonalMessage)(Buffer.from(message));
    const recoveredPubKey = (0, ethereumjs_util_1.ecrecover)(bufSigned, signature.v[0], Buffer.from(signature.r), Buffer.from(signature.s));
    return (0, ethereumjs_util_1.bufferToHex)((0, ethereumjs_util_1.pubToAddress)(recoveredPubKey));
}
exports.getEcRecoverMeta = getEcRecoverMeta;
function parseHexString(str) {
    const result = [];
    while (str.length >= 2) {
        result.push(parseInt(str.substring(0, 2), 16));
        str = str.substring(2, str.length);
    }
    return result;
}
exports.parseHexString = parseHexString;
function isSameAddress(address1, address2) {
    return address1.toLowerCase() === address2.toLowerCase();
}
exports.isSameAddress = isSameAddress;
async function sleep(ms) {
    return await new Promise(resolve => setTimeout(resolve, ms));
}
exports.sleep = sleep;
function ether(n) {
    return new bn_js_1.default((0, Web3JSUtils_1.toWei)(n, 'ether'));
}
exports.ether = ether;
function randomInRange(min, max) {
    return Math.floor(Math.random() * (max - min) + min);
}
exports.randomInRange = randomInRange;
function eventsComparator(a, b) {
    if (a.blockNumber === b.blockNumber) {
        return b.transactionIndex - a.transactionIndex;
    }
    return b.blockNumber - a.blockNumber;
}
exports.eventsComparator = eventsComparator;
function isSecondEventLater(a, b) {
    return eventsComparator(a, b) > 0;
}
exports.isSecondEventLater = isSecondEventLater;
function getLatestEventData(events) {
    if (events.length === 0) {
        return;
    }
    const eventDataSorted = events.sort(eventsComparator);
    return eventDataSorted[0];
}
exports.getLatestEventData = getLatestEventData;
function boolString(bool) {
    return bool ? chalk_1.default.green('good'.padEnd(14)) : chalk_1.default.red('wrong'.padEnd(14));
}
exports.boolString = boolString;
function getDataAndSignature(tx, chainId) {
    if (tx.to == null) {
        throw new Error('tx.to must be defined');
    }
    if (tx.s == null || tx.r == null || tx.v == null) {
        throw new Error('tx signature must be defined');
    }
    const input = [(0, ethereumjs_util_1.bnToUnpaddedBuffer)(tx.nonce)];
    if (!tx.supports(tx_1.Capability.EIP1559FeeMarket)) {
        input.push((0, ethereumjs_util_1.bnToUnpaddedBuffer)(tx.gasPrice));
    }
    else {
        input.push((0, ethereumjs_util_1.bnToUnpaddedBuffer)(tx.maxPriorityFeePerGas), (0, ethereumjs_util_1.bnToUnpaddedBuffer)(tx.maxFeePerGas));
    }
    input.push((0, ethereumjs_util_1.bnToUnpaddedBuffer)(tx.gasLimit), tx.to.toBuffer(), (0, ethereumjs_util_1.bnToUnpaddedBuffer)(tx.value), tx.data, (0, ethereumjs_util_1.toBuffer)(chainId), (0, ethereumjs_util_1.unpadBuffer)((0, ethereumjs_util_1.toBuffer)(0)), (0, ethereumjs_util_1.unpadBuffer)((0, ethereumjs_util_1.toBuffer)(0)));
    let vInt = tx.v.toNumber();
    if (vInt > 28) {
        vInt -= chainId * 2 + 8;
    }
    const data = `0x${(0, rlp_1.encode)(input).toString('hex')}`;
    const signature = signatureRSV2Hex(tx.r, tx.s, vInt);
    return {
        data,
        signature
    };
}
exports.getDataAndSignature = getDataAndSignature;
function signedTransactionToHash(signedTransaction, transactionOptions) {
    return (0, ethereumjs_util_1.bufferToHex)(tx_1.TransactionFactory.fromSerializedData((0, ethereumjs_util_1.toBuffer)(signedTransaction), transactionOptions).hash());
}
exports.signedTransactionToHash = signedTransactionToHash;
/**
 * remove properties with null (or undefined) value
 * (does NOT handle inner arrays)
 * @param obj - object to clean
 * @param recursive - descend into inner objects
 */
function removeNullValues(obj, recursive = false) {
    const c = {};
    Object.assign(c, obj);
    for (const k of Object.keys(c)) {
        if (c[k] == null) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete c[k];
        }
        else if (recursive) {
            let val = c[k];
            if (typeof val === 'object' && !Array.isArray(val) && !bn_js_1.default.isBN(val)) {
                val = removeNullValues(val, recursive);
            }
            c[k] = val;
        }
    }
    return c;
}
exports.removeNullValues = removeNullValues;
function formatTokenAmount(balance, decimals, tokenAddress, tokenSymbol) {
    let shiftedBalance;
    const tokenDecimals = (0, Web3JSUtils_1.toBN)(decimals.toString());
    if (tokenDecimals.eqn(18)) {
        shiftedBalance = balance;
    }
    else if (tokenDecimals.ltn(18)) {
        const shift = (0, Web3JSUtils_1.toBN)(18).sub(tokenDecimals);
        shiftedBalance = balance.mul((0, Web3JSUtils_1.toBN)(10).pow(shift));
    }
    else {
        const shift = tokenDecimals.subn(18);
        shiftedBalance = balance.div((0, Web3JSUtils_1.toBN)(10).pow(shift));
    }
    let shortTokenAddress = '';
    if (tokenAddress != null) {
        shortTokenAddress = `(${tokenAddress.substring(0, 6)}...${tokenAddress.substring(39)})`;
    }
    return `${(0, Web3JSUtils_1.fromWei)(shiftedBalance)} ${tokenSymbol} ${shortTokenAddress}`;
}
exports.formatTokenAmount = formatTokenAmount;
function splitRelayUrlForRegistrar(url, partsCount = 3) {
    var _a, _b;
    const maxLength = 32 * partsCount;
    if (url.length > maxLength) {
        throw new Error(`The URL does not fit to the RelayRegistrar. Please shorten it to less than ${maxLength} characters. The provided URL is: ${url}`);
    }
    const parts = (_a = url.match(/.{1,32}/g)) !== null && _a !== void 0 ? _a : [];
    const result = [];
    for (let i = 0; i < partsCount; i++) {
        result.push(`0x${Buffer.from((_b = parts[i]) !== null && _b !== void 0 ? _b : '').toString('hex').padEnd(64, '0')}`);
    }
    return result;
}
exports.splitRelayUrlForRegistrar = splitRelayUrlForRegistrar;
function packRelayUrlForRegistrar(parts) {
    return Buffer.from(parts.join('')
        .replace(/0x/g, '')
        .replace(/(00)+$/g, ''), 'hex').toString();
}
exports.packRelayUrlForRegistrar = packRelayUrlForRegistrar;
function toNumber(numberish) {
    switch (typeof numberish) {
        case 'string':
            return parseFloat(numberish);
        case 'number':
            return numberish;
        case 'bigint':
            return Number(numberish);
        case 'object':
            if ((0, Web3JSUtils_1.isBigNumber)(numberish)) {
                // @ts-ignore
                return numberish.toNumber();
            }
            throw new Error(`unsupported object of type ${numberish.constructor.name}`);
        default:
            throw new Error(`unsupported type ${typeof numberish}`);
    }
}
exports.toNumber = toNumber;
function getRelayRequestID(relayRequest, signature) {
    const types = ['address', 'uint256', 'bytes'];
    const parameters = [relayRequest.request.from, relayRequest.request.nonce, signature];
    const abiCoder = new ethers_1.ethers.utils.AbiCoder();
    const hash = (0, utils_1.keccak256)(abiCoder.encode(types, parameters));
    const rawRelayRequestId = removeHexPrefix(hash).padStart(64, '0');
    const prefixSize = 8;
    const prefixedRelayRequestId = rawRelayRequestId.replace(new RegExp(`^.{${prefixSize}}`), '0'.repeat(prefixSize));
    return `0x${prefixedRelayRequestId}`;
}
exports.getRelayRequestID = getRelayRequestID;
function getERC165InterfaceID(abi) {
    let interfaceId = abi
        .filter(it => it.type === 'function' && it.name != null)
        .map(it => {
        const iface = new abi_1.Interface([it]);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return iface.getSighash(it.name);
    })
        .filter(it => it !== '0x01ffc9a7') // remove the IERC165 method itself
        .map((x) => parseInt(x, 16))
        .reduce((x, y) => x ^ y);
    interfaceId = interfaceId > 0 ? interfaceId : 0xFFFFFFFF + interfaceId + 1;
    return '0x' + interfaceId.toString(16).padStart(8, '0');
}
exports.getERC165InterfaceID = getERC165InterfaceID;
function shuffle(array) {
    let currentIndex = array.length;
    let randomIndex;
    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
        // Pick a remaining element.
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]
        ];
    }
    return array;
}
exports.shuffle = shuffle;
/**
 * Wait for an array of promises.
 * After the first successful result, wait for "graceTime" period when the rest of the promises can still resolve.
 * Select a "winner" at random one of those successfully resolved promises.
 * @param promises - all promises we try to resolve
 * @param errorKeys - keys used to map errors to promises
 * @param graceTime - how long to wait after first successful result, in milliseconds
 * @param random  - Math.random-equivalent function (use for testing)
 * @returns - filled {@link WaitForSuccessResults} information
 */
async function waitForSuccess(promises, errorKeys, graceTime) {
    if (promises.length !== errorKeys.length) {
        throw new Error('Invalid errorKeys length');
    }
    for (let i = 0; i < errorKeys.length; i++) {
        const indexOfKey = errorKeys.indexOf(errorKeys[i]);
        if (indexOfKey !== i) {
            throw new Error('waitForSuccess: duplicate relay URL keys, aborting');
        }
    }
    return await new Promise((resolve) => {
        const ret = {
            errors: new Map(),
            results: []
        };
        function complete() {
            resolve(ret);
        }
        for (let i = 0; i < promises.length; i++) {
            promises[i]
                .then(result => {
                ret.results.push(result);
                if (ret.results.length === 1) {
                    setTimeout(complete, graceTime);
                }
            })
                .catch(err => {
                ret.errors.set(errorKeys[i], err);
            })
                .finally(() => {
                if (ret.results.length + ret.errors.size === promises.length) {
                    complete();
                }
            });
        }
    });
}
exports.waitForSuccess = waitForSuccess;
function pickRandomElementFromArray(arrayIn, random = Math.random) {
    return arrayIn[Math.floor(random() * arrayIn.length)];
}
exports.pickRandomElementFromArray = pickRandomElementFromArray;
/**
 * @return newValue - the best value to use as a gas parameter:
 *          the one RelayProvider used if it is above RelayServer minimum, the minimum otherwise
 * @return deltaPercent - the difference between input and result, in percents
 */
function adjustGasCostParameterUp(clientInput, pingResponseMinimum) {
    if (clientInput >= pingResponseMinimum) {
        return { newValue: clientInput, deltaPercent: 0 };
    }
    const deltaPercent = Math.round(((pingResponseMinimum - clientInput) * 100) / clientInput);
    return { newValue: pingResponseMinimum, deltaPercent };
}
exports.adjustGasCostParameterUp = adjustGasCostParameterUp;
/**
 * The RelayServer may respond with a {@link PingResponse} with a response that will require adjusting some parameters.
 * @return - a {@link RelayRequest} with parameters that should satisfy the RelayServer,
 *           or null if the required gas fees are different more than the {@link gasPriceFactorPercent}.
 */
function adjustRelayRequestForPingResponse(feesIn, relayInfo, logger) {
    const maxPriorityFeePerGas = adjustGasCostParameterUp(parseInt(feesIn.maxPriorityFeePerGas), parseInt(relayInfo.pingResponse.minMaxPriorityFeePerGas));
    let maxFeePerGas = adjustGasCostParameterUp(parseInt(feesIn.maxFeePerGas), parseInt(relayInfo.pingResponse.minMaxFeePerGas));
    if (maxPriorityFeePerGas.newValue > maxFeePerGas.newValue) {
        logger.warn(`Attention: for relay ${relayInfo.relayInfo.relayUrl} had to adjust 'maxFeePerGas' to be equal 'maxPriorityFeePerGas' (from ${maxFeePerGas.newValue} to ${maxPriorityFeePerGas.newValue}) to avoid RPC error.`);
        // reusing 'adjustGasCostParameterUp' but with new priority fee as minimum to get correct 'deltaPercent' calculation for the sorting
        maxFeePerGas = adjustGasCostParameterUp(parseInt(feesIn.maxFeePerGas), maxPriorityFeePerGas.newValue);
    }
    const updatedGasFees = {
        maxPriorityFeePerGas: (0, Web3JSUtils_1.toHex)(maxPriorityFeePerGas.newValue),
        maxFeePerGas: (0, Web3JSUtils_1.toHex)(maxFeePerGas.newValue)
    };
    const maxDeltaPercent = Math.max(maxFeePerGas.deltaPercent, maxPriorityFeePerGas.deltaPercent);
    return {
        relayInfo,
        maxDeltaPercent,
        updatedGasFees
    };
}
exports.adjustRelayRequestForPingResponse = adjustRelayRequestForPingResponse;
function averageBN(array) {
    const sum = array.reduce((a, v) => a.add(v));
    return sum.divn(array.length);
}
exports.averageBN = averageBN;
function validateRelayUrl(relayUrl) {
    let url;
    try {
        url = new URL(relayUrl);
    }
    catch (error) {
        return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
}
exports.validateRelayUrl = validateRelayUrl;
function wrapWeb3JsProvider(provider) {
    if (typeof provider === 'object' && typeof provider.getSigner !== 'function') {
        return new providers_1.Web3Provider(provider);
    }
    return provider;
}
exports.wrapWeb3JsProvider = wrapWeb3JsProvider;
function appendSlashTrim(urlInput) {
    urlInput = urlInput.trim();
    if (urlInput[urlInput.length - 1] !== '/') {
        urlInput += '/';
    }
    return urlInput;
}
exports.appendSlashTrim = appendSlashTrim;
//# sourceMappingURL=Utils.js.map